/**
 * Presentation helper for the dealer Potential Savings headline.
 *
 * The savings engine defines three classifications that must NEVER be
 * combined into a single number (see server/lib/savings/summary.ts):
 *   - verified_avoidable_cost
 *   - estimated_operating_savings
 *   - potential_upside (speculative — must not be presented as savings)
 *
 * This helper turns the API summary into separately labeled category rows.
 * There is intentionally no combined-total output.
 */

export interface ApiSavingsSummary {
  verifiedAvoidableCostLowCents: number;
  verifiedAvoidableCostHighCents: number;
  estimatedOperatingSavingsLowCents: number;
  estimatedOperatingSavingsHighCents: number;
  potentialUpsideLowCents: number;
  potentialUpsideHighCents: number;
  eligibleImpactCount: number;
  excludedImpactCount: number;
}

export interface SavingsCategoryRow {
  /** i18n key suffix under savings:headline.category.* */
  key: "verifiedAvoidableCost" | "estimatedOperatingSavings" | "potentialUpside";
  lowCents: number;
  highCents: number;
}

/**
 * Returns one row per classification with a nonzero high bound, preserving
 * the engine's category boundaries. Never sums across categories.
 */
export function savingsCategoryRows(summary: ApiSavingsSummary | null | undefined): SavingsCategoryRow[] {
  if (!summary) return [];
  const rows: SavingsCategoryRow[] = [
    {
      key: "verifiedAvoidableCost",
      lowCents: summary.verifiedAvoidableCostLowCents ?? 0,
      highCents: summary.verifiedAvoidableCostHighCents ?? 0,
    },
    {
      key: "estimatedOperatingSavings",
      lowCents: summary.estimatedOperatingSavingsLowCents ?? 0,
      highCents: summary.estimatedOperatingSavingsHighCents ?? 0,
    },
    {
      key: "potentialUpside",
      lowCents: summary.potentialUpsideLowCents ?? 0,
      highCents: summary.potentialUpsideHighCents ?? 0,
    },
  ];
  return rows.filter((r) => r.highCents > 0 || r.lowCents > 0);
}
